<template>
  <v-container p-0>
    <v-card :class="{ border: getDirtyState() }">
      <v-container class="text-center">
        <v-card-title class="justify-center">
          <div
            class="font-weight-thin headline"
            :class="[getDirtyState() ? 'warning--text' : 'blue--text']"
          >
            RESULTS
          </div>
        </v-card-title>

        <icon
          scale="4"
          class="grey--text lighten-4"
          v-if="!getDirtyState()"
          name="chart-bar"
        />
        <icon
          scale="4.5"
          class="warning--text lighten-4"
          v-if="getDirtyState()"
          name="exclamation-triangle"
        />
        <!--<v-divider class="m-1"></v-divider>-->
        <v-card-text class="px-4 pb-0 grey--text darken-4">
          <h5 class="headline font-weight-thin">
            {{ getResultNumber()
            }}<small class="font-weight-medium">{{ getResultText() }}</small>
          </h5>
          <v-row
            v-if="
              currentResultsLog &&
              currentResultsLog.errors &&
              currentResultsLog.errors.length
            "
            class="justify-center"
          >
            <v-dialog v-model="dialog" width="700">
              <template v-slot:activator="{ on: dialog }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on: tooltip }">
                    <v-btn
                      icon
                      class="m-0 bug-button"
                      v-on="{ ...tooltip, ...dialog }"
                    >
                      <icon
                        class="white--text"
                        scale="1.5"
                        name="user-injured"
                      />
                    </v-btn>
                  </template>
                  <span>Bug report</span>
                </v-tooltip>
              </template>
              <error-log-display
                :root="_self"
                :computationLog="currentResultsLog"
              ></error-log-display>
            </v-dialog>
          </v-row>
          <v-row class="caption justify-center px-4" v-if="isAnalyticalMission">
            <v-col>
              <i class="material-icons success--text subheading">done</i>
              <p>{{ successCount }} success</p>
            </v-col>
            <v-col>
              <i class="material-icons error--text subheading">close</i>
              <p>{{ errorCount }} errors</p>
            </v-col>
          </v-row>
          <v-row class="caption justify-center px-4" v-if="isNumericalMission">
            <v-col v-if="errorCount > 0">
              <p>Error</p>
            </v-col>
          </v-row>
          <v-row
            class="caption justify-center warning--text"
            v-if="getDirtyState()"
          >
            <v-container>
              <v-row justify="center">
                <progress-dialog
                  :computing.sync="computing"
                  :workInProgress.sync="workInProgress"
                  :text="'Start Recompute'"
                  :button-color="'warning'"
                ></progress-dialog>
              </v-row>
              <p class="mb-1 mt-2" v-if="!workInProgress">
                You have changed some component of the mission. This results
                does not match the current inputs. Please recompute the mission
                to update.
              </p>
              <p v-if="workInProgress && computing">Computing ...</p>
              <p v-if="workInProgress && !computing">Loading results ...</p>
            </v-container>
          </v-row>
        </v-card-text>
        <slot><!-- extra components --></slot>
        <v-card-actions class="justify-center pt-3">
          <v-dialog
            v-model="exportDialog"
            width="800"
            v-if="canExport && !disableExport"
          >
            <template v-slot:activator="{ on }">
              <v-btn
                text
                outlined
                color="blue darken-1"
                @click="exportDialog = true"
                v-on="on"
                >Export</v-btn
              >
            </template>
            <v-card>
              <v-toolbar class="grey-text">
                <icon scale="1.5" name="file-export" />
                <v-toolbar-title>Export Results</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon small class="m-0" @click="exportDialog = false">
                  <v-icon>close</v-icon>
                </v-btn>
              </v-toolbar>
              <v-alert outlined type="info" :value="true"
                >Results are exported in the order they are selected.</v-alert
              >
              <v-alert
                v-if="isDeploymentMission"
                outlined
                type="warning"
                :value="true"
              >
                One line will be exported for each launch used in a
                configuration, that means several lines per configuration.
                Results should be analyzed by grouping by "Launch Sequences
                Name".
              </v-alert>
              <v-row no-gutters>
                <v-col>
                  <v-card-text>
                    <v-treeview
                      v-model="exportTree"
                      :items="items"
                      active-class="grey lighten-4 indigo--text"
                      open-on-click
                      selectable
                      expand-icon="expand_more"
                      :open.sync="open"
                    >
                      <template v-slot:label="{ item }">
                        <p class="my-1 grey-text">{{ item.label }}</p>
                      </template>
                    </v-treeview>
                  </v-card-text>
                </v-col>
                <v-divider vertical></v-divider>
                <v-col cols="4">
                  <v-card-text>
                    <div
                      class="subheading accent-1 pa-3 text-xs-center"
                      style="color: #5757ff"
                    >
                      Additional configuration
                    </div>
                    <v-row>
                      <p class="body-2 my-1 grey-text">Export orientation</p>
                    </v-row>
                    <v-row no-gutters>
                      <v-col>
                        <v-radio-group class="ma-0" v-model="csvOrientation">
                          <v-radio
                            label="Horizontal"
                            value="horizontal"
                          ></v-radio>
                          <v-radio label="Vertical" value="vertical"></v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>
                    <v-row>
                      <p class="body-2 my-2 grey-text">CSV delimiter</p>
                    </v-row>
                    <v-row no-gutters>
                      <v-col>
                        <v-radio-group
                          row
                          class="ma-0"
                          v-model="csvDelimiter"
                          v-for="option in delimitersOptions"
                          :key="option.value"
                        >
                          <v-radio
                            style="height: 10px"
                            :label="option.label"
                            :value="option.value"
                          ></v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>
                    <v-row>
                      <p class="body-2 my-2 grey-text">Float separator</p>
                    </v-row>
                    <v-row no-gutters>
                      <v-col>
                        <v-radio-group
                          row
                          class="ma-0"
                          v-model="floatSeparator"
                          v-for="option in floatSeparatorOptions"
                          :key="option.value"
                        >
                          <v-radio
                            style="height: 10px"
                            :disabled="
                              option.value === 'comma' && csvDelimiter === ','
                            "
                            :label="option.label"
                            :value="option.value"
                          ></v-radio>
                        </v-radio-group>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-col>
              </v-row>
              <v-divider class="my-1"></v-divider>
              <v-card-actions>
                <v-btn class="grey-text" @click="exportTree = []" text
                  >Reset</v-btn
                >
                <v-spacer></v-spacer>
                <v-btn
                  :disabled="!this.exportTree || this.exportTree.length === 0"
                  class="white--text"
                  color="primary"
                  depressed
                  @click.stop="exportData()"
                >
                  <icon scale="1.5" class="pr-2" name="file-export" />
                  Export
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-dialog
            v-model="resultViewDialog"
            fullscreen
            transition="dialog-bottom-transition"
          >
            <template v-slot:activator="{ on }">
              <v-btn color="primary" class="ml-3" v-on="on">View</v-btn>
            </template>
            <v-card
              :class="$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-5'"
            >
              <v-toolbar dark color="black">
                <v-toolbar-title>Detailed View</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon dark class="m-0" @click="resultViewDialog = false">
                  <v-icon>close</v-icon>
                </v-btn>
              </v-toolbar>
              <analytical-result-display
                :missionManager="root"
                :results="currentResultsLog.results"
                v-if="currentResultsLog != null && isAnalyticalMission"
              ></analytical-result-display>
              <numerical-result-display
                :additionalData="additionalData"
                :missionManager="root"
                :results="currentResultsLog.results"
                v-if="currentResultsLog != null && isNumericalMission"
              ></numerical-result-display>
              <deployment-result-display
                :missionManager="root"
                :results="currentResultsLog.results"
                v-if="currentResultsLog != null && isDeploymentMission"
              ></deployment-result-display>
            </v-card>
          </v-dialog>
        </v-card-actions>
        <v-card-actions
          class="justify-center pt-3"
          v-if="scriptingOptionActivated"
        >
          <results-api-script
            :results="currentResultsLog.results"
          ></results-api-script>
        </v-card-actions>
        <v-card-actions
          class="justify-center pt-3"
          v-if="this.currentResultsLog.results.linkBudgetEvents"
        >
          <v-btn
            class="white--text"
            color="primary"
            depressed
            @click.stop="exportLinkBudgetEvents()"
          >
            <icon scale="1.5" class="pr-2" name="file-export" />
            Link budget events
          </v-btn>
        </v-card-actions>
      </v-container>
    </v-card>
  </v-container>
</template>

<script>
import Papa from 'papaparse'
import ProgressDialog from './ProgressDialog'
import dataTools from '../../tools/data.js'
import { mapGetters } from 'vuex'
import plotInput from '../../mixins/plotInput.js'
import ResultsApiScript from './ResultsApiScript.vue'

export default {
  name: 'results-card',
  components: { ProgressDialog, ResultsApiScript },
  props: {
    root: Object,
    disableExport: {
      type: Boolean,
      default: false
    },
    additionalData: {
      type: Object,
      default: null
    }
  },
  mixins: [plotInput],
  computed: {
    ...mapGetters(['currentMission', 'currentResultsLog', 'currentUser']),
    scriptingOptionActivated() {
      return (
        this?.currentUser?.options != null &&
        this.currentUser.options.includes('SCRIPTING')
      )
    },
    successCount() {
      if (
        !this.currentResultsLog ||
        !this.currentResultsLog.errors ||
        this.currentResultsLog.resultCount <= 0
      ) {
        return 0
      }
      return this.currentResultsLog.successCount
    },
    canExport() {
      if (this.currentResultsLog && this.currentResultsLog.results) {
        if (this.isAnalyticalMission) {
          return this.currentResultsLog.results.length > 0
        } else if (this.isNumericalMission) {
          return (
            this.currentResultsLog.results.ephemerides &&
            this.currentResultsLog.results.ephemerides.length > 0
          )
        } else if (this.isDeploymentMission) {
          return this.currentResultsLog.results != null
        }
      }
      return false
    },
    errorCount() {
      if (!this.currentResultsLog || !this.currentResultsLog.errors) {
        return 0
      }
      return this.currentResultsLog.errors.length
    },
    items() {
      let children = []
      if (this.currentResultsLog.results) {
        if (this.isAnalyticalMission) {
          children = this.listTreeOptions(
            this.currentResultsLog.results,
            'ParametricStudyResult'
          )
        } else if (this.isNumericalMission) {
          if (this.currentResultsLog.results.ephemerides) {
            children = this.listTreeOptions(
              this.currentResultsLog.results.ephemerides,
              'SimulationResult',
              this.currentResultsLog.results.fieldIndexes
            )
          }
        } else if (this.isDeploymentMission) {
          children = this.listTreeOptions(
            [this.currentResultsLog.results],
            'DeploymentResult'
          )
        }
      }
      return [
        {
          id: 'all',
          label: 'Select All',
          children
        }
      ]
    },
    isDeploymentMission() {
      return (
        this.currentMission != null &&
        this.currentMission.typeName === 'DeploymentRequest'
      )
    },

    isAnalyticalMission() {
      return (
        this.currentMission != null &&
        this.currentMission.typeName === 'ParametricStudyRequest'
      )
    },

    isNumericalMission() {
      return (
        this.currentMission != null &&
        this.currentMission.typeName === 'SimulationRequest'
      )
    }
  },
  watch: {
    csvDelimiter: function (value) {
      if (value === ',') {
        this.floatSeparator = 'point'
      }
    }
  },
  data() {
    return {
      dialog: false,
      resultViewDialog: false,
      computing: false,
      workInProgress: false,
      exportDialog: false,
      exportTree: [],
      open: ['all'],
      csvOrientation: 'horizontal',
      floatSeparator: 'point',
      floatSeparatorOptions: [
        {
          label: 'Point',
          value: 'point'
        },
        {
          label: 'Comma',
          value: 'comma'
        }
      ],
      csvDelimiter: ';',
      delimitersOptions: [
        {
          label: 'Semicolon',
          value: ';'
        },
        {
          label: 'Comma',
          value: ','
        },
        {
          label: 'Tab',
          value: '\t'
        }
      ]
    }
  },
  methods: {
    getResultNumber() {
      if (this.isAnalyticalMission) {
        return this.currentResultsLog.resultCount
      } else if (this.isNumericalMission) {
        if (this.currentResultsLog.results.ephemerides != null) {
          return this.currentResultsLog.results.ephemerides.length
        }
      } else if (this.isDeploymentMission) {
        if (
          this.currentResultsLog &&
          this.currentResultsLog.results &&
          this.currentResultsLog.results.launchConfigurations != null
        ) {
          return this.currentResultsLog.results.launchConfigurations.length
        }
      }
      return 0
    },

    getResultText() {
      if (this.isAnalyticalMission) {
        if (this.currentResultsLog.resultCount <= 1) {
          return ' RESULT'
        }
        return ' RESULTS'
      } else if (this.isNumericalMission) {
        if (
          this.currentResultsLog &&
          this.currentResultsLog.results &&
          this.currentResultsLog.results.ephemerides != null
        ) {
          if (this.currentResultsLog.results.ephemerides.length > 1) {
            return ' EPHEMERIDES'
          } else {
            return ' EPHEMERIDE'
          }
        }
        return ' EPHEMERIDE'
      } else if (this.isDeploymentMission) {
        if (
          this.currentResultsLog &&
          this.currentResultsLog.results &&
          this.currentResultsLog.results.launchConfigurations != null
        ) {
          if (this.currentResultsLog.results.launchConfigurations.length > 1) {
            return ' CONFIGURATIONS'
          } else {
            return ' CONFIGURATION'
          }
        }
        return ' CONFIGURATION'
      }
    },

    getDirtyState() {
      return this.currentMission.dirty
    },

    makeTreeSubListFlat(results) {
      let flatTree = []
      if (Array.isArray(results) && results.length > 0) {
        for (let subResult of results) {
          flatTree = flatTree.concat(this.makeTreeSubListFlat(subResult))
        }
      } else {
        // object
        let keys = Object.keys(results)
        let subResultAdded = false
        for (let key of keys) {
          let branch = results[key]
          if (typeof branch === 'string') {
            // leaf
          } else if (Array.isArray(branch) && branch.length > 1) {
            for (let subBranchIndex in branch) {
              let subBranch = branch[subBranchIndex]
              let subTrees = this.makeTreeSubListFlat(subBranch)
              for (let subTree of subTrees) {
                let copyOfResult = JSON.parse(JSON.stringify(results)) // copy values, not ref to not modify source
                copyOfResult[key] = subTree // To be treated as object later
                flatTree = flatTree.concat(
                  this.makeTreeSubListFlat(copyOfResult)
                )
                subResultAdded = true
              }
            }
          } else {
            let subTrees = this.makeTreeSubListFlat(branch)
            if (subTrees.length > 1) {
              for (let subTree of subTrees) {
                let copyOfResult = JSON.parse(JSON.stringify(results)) // copy values, not ref to not modify source
                copyOfResult[key] = subTree
                flatTree = flatTree.concat(
                  this.makeTreeSubListFlat(copyOfResult)
                )
                subResultAdded = true
              }
            } // else no sub tree, single path
          }
        }
        if (!subResultAdded) {
          flatTree.push(results)
        }
      }
      return flatTree
    },

    getDataToExport() {
      let resultToExport = []
      let context = ''
      let results = []
      if (this.isAnalyticalMission) {
        context = 'ParametricStudyResult'
        results = this.currentResultsLog.results
      } else if (this.isNumericalMission) {
        context = 'SimulationResult'
        results = dataTools.convertSimulationData(
          this.currentResultsLog.results.ephemerides,
          this.currentResultsLog.results.fieldIndexes
        )
      } else if (this.isDeploymentMission) {
        context = 'DeploymentResult'
        results = this.makeTreeSubListFlat(this.currentResultsLog.results)
      } else {
        console.log('Error : this mission can not be exported')
      }

      this.exportTree.forEach((field) => {
        if (field !== 'all') {
          let fieldPath = []
          if (this.isAnalyticalMission) {
            fieldPath = field.split('.')
          } else if (this.isNumericalMission) {
            fieldPath = [field.replace('.', '')]
          } else if (this.isDeploymentMission) {
            fieldPath = field.split('.')
          }
          let csvRow = this.getAllResults(fieldPath, context, results)
          if (csvRow !== undefined) {
            resultToExport.push(csvRow)
          }
        }
      })
      if (this.csvOrientation === 'horizontal') {
        return resultToExport[0].map((col, i) =>
          resultToExport.map((row) => row[i])
        )
      }
      return resultToExport
    },

    getAllResults(path, context, results) {
      let fieldLabel = dataTools.getFieldLabelFromPath(path, context)

      // TODO side effect to improve : this test detects that the sub tree has no label defined
      if (fieldLabel === undefined || fieldLabel === '') {
        return
      }
      fieldLabel = dataTools.getDataLabel(path, context)
      let fieldUnit = dataTools.getSiUnit(
        dataTools.getFieldTypeFromPath(
          path,
          context,
          this.currentResultsLog.scriptConfig
        )
      )
      fieldUnit = fieldUnit !== '' ? ' (' + fieldUnit + ')' : fieldUnit
      let list = [fieldLabel + fieldUnit]
      results.forEach((result) => {
        let fieldValues = this.getFieldValues(path.slice(), result)
        list = list.concat(fieldValues)
      })
      return list
    },

    getFieldValues(path, result) {
      let field = path.shift()
      if (result[field] == null) {
        if (Array.isArray(result) && result.length > 0) {
          path.unshift(field) // for sub analysis
          let values = []
          for (let subResult of result) {
            let initPath = path.slice() // copy to not edit the original path
            values = values.concat(this.getFieldValues(initPath, subResult))
          }
          return values
        } else {
          console.log('Error reading ' + path + ' in results')
        }
      }

      if (Array.isArray(path) && path.length) {
        return this.getFieldValues(path, result[field])
      }
      if (this.floatSeparator === 'comma') {
        if (this.isFloat(result[field])) {
          return result[field].toString().replace('.', ',')
        }
      }
      return result[field]
    },

    isFloat(n) {
      return Number(n) === n && n % 1 !== 0
    },

    exportData() {
      if (this.exportTree && this.exportTree.length !== 0) {
        let dataToExport = this.getDataToExport()

        let csv = Papa.unparse(dataToExport, { delimiter: this.csvDelimiter })

        dataTools.downloadFile(this.currentMission.name, csv)
      }
    },

    exportLinkBudgetEvents() {
      if (this.currentResultsLog.results?.linkBudgetEvents) {
        let csv = Papa.unparse(
          this.currentResultsLog.results?.linkBudgetEvents,
          { delimiter: this.csvDelimiter }
        )
        dataTools.downloadFile(this.currentMission.name + 'link_budget', csv)
      }
    }
  }
}
</script>

<style scoped>
.bug-button {
  background: #ff5252 !important;
}

.border {
  border-style: groove !important;
  border-color: #fb8c0073 !important;
  border-width: 3px !important;
}

.grey-text {
  color: #757575;
}
</style>
